import {
  BrokeredOrderStatus,
  enumBrokeredOrderSortOption,
} from '@rematter/frontend-lib-api-client';

import { createEntityComboBox } from '~/shared/components/select-fields/helpers/createEntityComboBox';
import { createQueryHook } from '~/shared/providers/api';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

export type SelectableBrokeredOrder = QueryHookResult<
  typeof useBrokeredOrdersList
>[number];

export const useBrokeredOrdersList = createQueryHook(
  'brokerage.selectBrokeredOrder',
  (params: {
    ids?: string[];
    searchTerm?: string;
    supplierIds?: string[];
    customerIds?: string[];
    statuses?: BrokeredOrderStatus[];
  }) => ({
    brokeredOrders: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          supplierIds: params.supplierIds,
          customerIds: params.customerIds,
          statuses: params.statuses,
        },
        sorting: [enumBrokeredOrderSortOption.CreatedAtDesc],
        pagination: {
          limit: 20,
          offset: 0,
        },
      },
      entries: {
        id: true,
        orderNumber: true,
        status: true,
        suppliers: {
          id: true,
          name: true,
        },
        customers: {
          id: true,
          name: true,
        },
      },
    },
  }),
  {
    select: (data) => data.brokeredOrders.entries ?? [],
  },
);

const formatBrokeredOrderLabel = (entity: SelectableBrokeredOrder): string => {
  const parts = [`#${entity.orderNumber}`];
  const supplierName = entity.suppliers?.[0]?.name;
  if (supplierName) {
    parts.push(`- ${supplierName}`);
  }
  return parts.join(' ');
};

const formatBrokeredOrderSubLabel = (
  entity: SelectableBrokeredOrder,
): string => {
  const parts: string[] = [];
  const customerName = entity.customers?.[0]?.name;
  if (customerName) {
    parts.push(`Customer: ${customerName}`);
  }
  parts.push(entity.status);
  return parts.join(' • ');
};

export const SelectBrokeredOrder = createEntityComboBox.remotelySearched(
  'SelectBrokeredOrder',
  useBrokeredOrdersList,
  {
    formatLabel: formatBrokeredOrderLabel,
    formatSubLabel: formatBrokeredOrderSubLabel,
    compactSelection: true,
  },
);
